import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { isNone } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { getInitials } from 'uplisting-frontend/utils/get-initials';

export enum UserRoles {
  accountOwner = 'account_owner',
  host = 'host',
  housekeeper = 'housekeeper',
  owner = 'owner',
}

export enum UserOnboardStatus {
  needsReview = 'needs_review',
  complete = 'complete',
}

export enum UserTrialStatus {
  notStarted = 'not_started',
  active = 'active',
  ended = 'ended',
  upgraded = 'upgraded',
}

export enum AirbnbGeneralConnectionLevel {
  full = 'full',
  readOnly = 'read_only',
  inactive = 'inactive',
  notConnected = 'not_connected',
}

export interface IUserJson {
  email: string;
  role: UserRoles;
}

export default class UserModel extends Model {
  @attr('string') name!: string;
  @attr('string') email!: string;
  @attr('string') role!: UserRoles;
  @attr('string') crispUserHash!: string;
  @attr('string') userFlowHash!: string;
  @attr('boolean') trialist!: boolean;
  @attr('string') trialStatus!: UserTrialStatus;
  @attr('date') trialEnd?: Date;
  @attr('date') lastActiveAt?: Date;
  @attr('number') unreadMessages!: number;
  @attr('number') unreadTeamMessages!: number;
  @attr('number') unreadTeamMentions!: number;
  @attr('boolean') lapsed!: boolean;
  @attr('number') pendingAlerts!: number;
  @attr('string') gaClientId!: string;
  @attr('string') gclid!: string;
  @attr('array') identities!: string[];
  @attr('string') invitation!: string;
  @attr('string') onboardStatus?: UserOnboardStatus;
  @attr('number') airdnaUserId?: number;
  @attr('boolean') airdnaRedirectClick!: boolean;
  @attr('boolean') wentOverSubscriptionPropertyLimit!: boolean;
  @attr('boolean') verified!: boolean;
  @attr('string') accountId!: string;
  @attr('date') createdAt!: Date;
  @attr('number') numberListings!: number;
  @attr('boolean') stripeConnected!: boolean;
  @attr('boolean') subscriptionActive!: boolean;
  @attr('string') vrboAdvertiserId?: string;
  @attr('boolean') directWebsiteEnabled?: boolean;
  @attr('string') airbnbGeneralConnectionLevel?: AirbnbGeneralConnectionLevel;
  @attr('number') numberAirbnbListings?: number;
  @attr('number') numberBdcListings?: number;
  @attr('number') numberHomeAwayListings?: number;
  @attr('boolean') protectFeatureActive?: boolean;
  @attr('boolean') identityFeatureActive?: boolean;
  @attr('boolean') esignFeatureActive?: boolean;
  @attr('boolean') clientManagementEnabled?: boolean;
  @attr('boolean') clientStatementsEnabled?: boolean;
  @attr('boolean') dynamicPricingSyncActive?: boolean;
  @attr('boolean') changeoverFeatureActive?: boolean;

  @belongsTo('client', { inverse: 'users', async: false })
  client!: ModelRegistry['client'];

  @belongsTo('customer', { inverse: 'user', async: false })
  customer?: ModelRegistry['customer'];

  @hasMany('permission', { inverse: 'user', async: false })
  permissions!: ModelRegistry['permission'][];

  @cached
  get initials(): string {
    return getInitials(this.name);
  }

  @cached
  get isAccountOwner(): boolean {
    return this.role === UserRoles.accountOwner;
  }

  @cached
  get isHousekeeper(): boolean {
    return this.role === UserRoles.housekeeper;
  }

  @cached
  get isHost(): boolean {
    return this.role === UserRoles.host;
  }

  @cached
  get isHostOrAccountOwner(): boolean {
    return this.isHost || this.isAccountOwner;
  }

  @cached
  get onboardRoute(): string | undefined {
    if (!this.isAccountOwner) {
      return;
    }

    if (this.isOnboardNeedsInfo) {
      return 'register.info';
    } else if (this.isOnboardNeedsReview) {
      return 'register.meeting';
    }
  }

  @cached
  get isOnboardNeedsInfo(): boolean {
    return isNone(this.onboardStatus);
  }

  @cached
  get isOnboardNeedsReview(): boolean {
    return this.onboardStatus === UserOnboardStatus.needsReview;
  }

  @cached
  get isOnboardComplete(): boolean {
    return this.onboardStatus === UserOnboardStatus.complete;
  }

  @cached
  get isAirdnaUser(): boolean {
    return !!this.airdnaUserId;
  }

  @cached
  get canListTeammates(): boolean {
    return (
      this.hasAccessTo('account.team') ||
      this.hasAccessTo('changeovers.assignTeammate')
    );
  }

  public hasAccessTo(permission: string): boolean {
    const record = this.permissions.find(
      (item) => item.permission === permission,
    );

    if (!record) {
      return false;
    }

    return record.permitted;
  }

  public toObject(): IUserJson {
    return {
      email: this.email,
      role: this.role,
    };
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    user: UserModel;
  }
}
